// @greenlight-spec: 28-it-dashboard-ui.md#policies--governance-policy-set
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import { MonoValue } from '@/components/greenlight/MonoValue';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useCursorPages } from '@/lib/useCursorPages';
import type { PolicyAppResult, PolicySetting } from '@greenlight/contract';

import { AuditRetentionSection } from './AuditRetentionSection';
import { ExternalChecksSection } from './ExternalChecksSection';
import { SegmentedControl } from './SegmentedControl';
import { SettingsSectionHeading } from './SettingsSectionHeading';

const LIST_KEY = ['policies'];

/**
 * Settings → Policies: tune the shipped deploy checks (on/off, block/warn, the
 * config values the rules already read) — no rule authoring; the authoring
 * lifecycle is the deferred management system (docs/38).
 */
export function PoliciesSettings(): React.JSX.Element {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = api.policies.list.useQuery(LIST_KEY, undefined, {
    queryKey: LIST_KEY,
  });
  const update = api.policies.update.useMutation();

  const refresh = () => queryClient.invalidateQueries({ queryKey: LIST_KEY });

  const runUpdate = async (
    policyId: string,
    body: {
      is_active?: boolean;
      enforcement?: 'block' | 'warn';
      config?: Record<string, unknown> | null;
    },
  ): Promise<void> => {
    try {
      const res = await update.mutateAsync({ params: { policy_id: policyId }, body });
      if (res.status >= 300) throw res;
      toast.success('Policy updated.');
      await refresh();
    } catch (err) {
      const message = (err as { body?: { message?: string } })?.body?.message;
      toast.error(message ?? "Couldn't update the policy.");
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="policies-loading">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    );
  }
  // A 401/403/500 is a settled failure here, not a pending load.
  if (isError || data?.status !== 200) {
    return (
      <Alert variant="destructive" data-testid="policies-error">
        <AlertTitle>Couldn&apos;t load policies</AlertTitle>
        <AlertDescription>Something went wrong fetching the deploy checks.</AlertDescription>
      </Alert>
    );
  }

  const { policies, recommended_base_images } = data.body;

  return (
    <div className="space-y-8" data-testid="policies-settings">
      <div>
        <SettingsSectionHeading title="Deploy checks" pill="IT" />
        <p className="mt-1 max-w-2xl text-[13px] leading-relaxed text-muted-foreground">
          Every merge is gated by these checks. Turn a check off, soften it to warn, or edit the
          values it compares against — changes apply to the next run.
        </p>
        <div className="mt-4 space-y-3">
          {policies.map((policy) => (
            <PolicyRow
              key={policy.id}
              policy={policy}
              recommendedImages={recommended_base_images}
              onUpdate={(body) => runUpdate(policy.id, body)}
            />
          ))}
        </div>
      </div>

      <ExternalChecksSection />

      <AuditRetentionSection />

      <PolicyResults />
    </div>
  );
}

function policyBadge(policy: PolicySetting): React.JSX.Element {
  if (!policy.is_active) return <StatusBadge variant="inactive">Off</StatusBadge>;
  if (policy.inactive_reason === 'awaiting_configuration') {
    return <StatusBadge variant="pending">Needs config</StatusBadge>;
  }
  return <StatusBadge variant="active">Active</StatusBadge>;
}

function PolicyRow({
  policy,
  recommendedImages,
  onUpdate,
}: {
  policy: PolicySetting;
  recommendedImages: string[];
  onUpdate: (body: {
    is_active?: boolean;
    enforcement?: 'block' | 'warn';
    config?: Record<string, unknown> | null;
  }) => Promise<void>;
}): React.JSX.Element {
  return (
    <div
      className="rounded-gl-lg border border-border bg-card"
      data-testid={`policy-row-${policy.id}`}
    >
      <div className="space-y-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[15px] font-bold tracking-tight text-foreground">
            {policy.name}
          </span>
          {policyBadge(policy)}
        </div>
        <p className="max-w-2xl text-[13px] leading-relaxed text-muted-foreground">
          {policy.description}
        </p>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <span className="inline-flex items-center gap-2">
            <Switch
              checked={policy.is_active}
              onCheckedChange={(checked) => void onUpdate({ is_active: checked })}
              aria-label={`${policy.name} active`}
            />
            <span className="text-[12.5px] text-muted-foreground">
              {policy.is_active ? 'On' : 'Off'}
            </span>
          </span>
          {/* An advisory check reports and never gates, and tuning only ever
              downgrades — so offering Block here would be a control that
              silently does nothing. Say what it does instead. */}
          {policy.advisory ? (
            <span className="text-[12.5px] text-muted-foreground" data-testid="policy-advisory">
              Always warns; this check never blocks a merge
            </span>
          ) : (
            <SegmentedControl
              options={[
                { value: 'block' as const, label: 'Block' },
                { value: 'warn' as const, label: 'Warn' },
              ]}
              value={policy.enforcement}
              onChange={(enforcement) => void onUpdate({ enforcement })}
              aria-label={`${policy.name} enforcement`}
            />
          )}
        </div>

        {policy.config_shape === 'images' && (
          <ImagesConfig
            policy={policy}
            recommendedImages={recommendedImages}
            onSave={(images) => onUpdate({ config: { images } })}
          />
        )}
        {policy.config_shape === 'caps' && (
          <CapsConfig policy={policy} onSave={(caps) => onUpdate({ config: { caps } })} />
        )}
      </div>
    </div>
  );
}

function ImagesConfig({
  policy,
  recommendedImages,
  onSave,
}: {
  policy: PolicySetting;
  recommendedImages: string[];
  onSave: (images: string[]) => Promise<void>;
}): React.JSX.Element {
  const stored = ((policy.config as { images?: string[] } | null)?.images ?? []).join('\n');
  const [draft, setDraft] = React.useState(stored);
  React.useEffect(() => setDraft(stored), [stored]);
  const parsed = draft
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  return (
    <div className="max-w-xl space-y-2">
      <Label htmlFor={`images-${policy.id}`}>Allowed base images (one per line)</Label>
      <Textarea
        id={`images-${policy.id}`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={5}
        placeholder="node:22-alpine"
        className="font-mono text-[12.5px]"
      />
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={parsed.length === 0 || draft === stored}
          onClick={() => void onSave(parsed)}
        >
          Save allowlist
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => void onSave(recommendedImages)}
          data-testid="use-recommended-images"
        >
          Use recommended list
        </Button>
      </div>
    </div>
  );
}

function CapsConfig({
  policy,
  onSave,
}: {
  policy: PolicySetting;
  onSave: (caps: { cpu?: number; memory?: number }) => Promise<void>;
}): React.JSX.Element {
  // Prefilled from the check's own defaults, never a literal: this form is shared
  // by the hard cap and the sizing advisory, whose defaults differ by an order of
  // magnitude, and saving without editing must write back what is already in force.
  const caps = (v: unknown): { cpu?: number; memory?: number } | undefined =>
    (v as { caps?: { cpu?: number; memory?: number } } | null)?.caps;
  const stored = caps(policy.config);
  const fallback = caps(policy.config_defaults);
  const [cpu, setCpu] = React.useState((stored?.cpu ?? fallback?.cpu)?.toString() ?? '');
  const [memory, setMemory] = React.useState(
    (stored?.memory ?? fallback?.memory)?.toString() ?? '',
  );

  const save = (): void => {
    const parsedCpu = Number(cpu);
    const parsedMemory = Number(memory);
    if (
      !Number.isFinite(parsedCpu) ||
      parsedCpu <= 0 ||
      !Number.isFinite(parsedMemory) ||
      parsedMemory <= 0
    ) {
      toast.error(
        policy.advisory
          ? 'Guidance values must be positive numbers.'
          : 'Caps must be positive numbers.',
      );
      return;
    }
    void onSave({ cpu: parsedCpu, memory: parsedMemory });
  };

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="space-y-1.5">
        <Label htmlFor={`cap-cpu-${policy.id}`}>
          {policy.advisory ? 'CPU guidance (vCPU)' : 'CPU cap (vCPU)'}
        </Label>
        <Input
          id={`cap-cpu-${policy.id}`}
          type="number"
          min="0.05"
          step="0.05"
          value={cpu}
          onChange={(e) => setCpu(e.target.value)}
          className="w-28"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`cap-mem-${policy.id}`}>
          {policy.advisory ? 'Memory guidance (GiB)' : 'Memory cap (GiB)'}
        </Label>
        <Input
          id={`cap-mem-${policy.id}`}
          type="number"
          min="0.25"
          step="0.25"
          value={memory}
          onChange={(e) => setMemory(e.target.value)}
          className="w-28"
        />
      </div>
      <Button size="sm" variant="outline" onClick={save}>
        {policy.advisory ? 'Save guidance' : 'Save caps'}
      </Button>
    </div>
  );
}

const CHECK_BADGE: Record<
  string,
  { variant: 'active' | 'failed' | 'pending' | 'inactive'; label: string }
> = {
  pass: { variant: 'active', label: 'Pass' },
  fail: { variant: 'failed', label: 'Fail' },
  running: { variant: 'pending', label: 'Running' },
  skip: { variant: 'inactive', label: 'Skipped' },
};

function PolicyResults(): React.JSX.Element {
  // Cursor-paginated (docs/16); pages accumulate behind a Show more button.
  const [cursor, setCursor] = React.useState<string | undefined>(undefined);
  const { data, isLoading, isError } = api.policies.results.useQuery(
    ['policies-results', cursor ?? null],
    { query: cursor ? { cursor } : {} },
    { queryKey: ['policies-results', cursor ?? null] },
  );
  const pages = useCursorPages<PolicyAppResult>({
    query: { data, isError, isLoading },
    cursor,
    onCursorChange: setCursor,
    getKey: (item) => item.app_id,
  });

  return (
    <div>
      <SettingsSectionHeading title="Apps failing their checks" pill="Read-only" />
      <p className="mt-1 max-w-2xl text-[13px] leading-relaxed text-muted-foreground">
        Apps whose most recent default-branch run failed, and which checks decided it.
      </p>
      <div className="mt-4" data-testid="policy-results">
        {pages.isLoadingFirstPage ? (
          <Skeleton className="h-24 w-full" />
        ) : pages.hasError ? (
          // Settled non-2xx (ts-rest resolves it into `data`) — an error, not a load.
          <Alert variant="destructive" data-testid="policy-results-error">
            <AlertTitle>Couldn&apos;t load results</AlertTitle>
            <AlertDescription>Something went wrong fetching the latest runs.</AlertDescription>
          </Alert>
        ) : pages.items.length === 0 ? (
          <p className="rounded-gl border border-border p-6 text-center text-[13px] text-muted-foreground">
            Nothing failing — every app&apos;s most recent run passed its checks.
          </p>
        ) : (
          <ul className="space-y-3">
            {pages.items.map((item) => (
              <li
                key={item.app_id}
                className="rounded-gl border border-border p-3"
                data-testid={`policy-result-${item.app_slug}`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[13.5px] font-semibold text-foreground">
                    {item.app_name}
                  </span>
                  <MonoValue>{item.commit_sha.slice(0, 10)}</MonoValue>
                  <span className="text-[11.5px] text-muted-foreground">
                    {item.completed_at
                      ? new Date(item.completed_at).toLocaleString()
                      : 'in progress'}
                  </span>
                </div>
                <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5">
                  {item.checks.map((check) => {
                    const badge = CHECK_BADGE[check.status] ?? CHECK_BADGE['skip']!;
                    return (
                      <li key={check.name} className="inline-flex items-center gap-1.5">
                        <MonoValue>{check.name}</MonoValue>
                        <StatusBadge variant={badge.variant}>{badge.label}</StatusBadge>
                        {check.status === 'fail' && check.error_summary && (
                          <span className="max-w-md truncate text-[11.5px] text-muted-foreground">
                            {check.error_summary}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        )}
        {pages.hasMore && (
          <div className="pt-3">
            <Button
              size="sm"
              variant="outline"
              onClick={pages.loadMore}
              disabled={pages.isLoadingMore}
            >
              {pages.isLoadingMore ? 'Loading…' : 'Show more'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
